

import React, { useState } from 'react';
import { Edit2, Trash2, Lock, Unlock, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { UserRoleBadge } from './UserRoleBadge';
import { UserDetails } from './UserDetails';
import { getUsersUrl, addUserRole } from '../../../api/ApiUser';
import { mutate } from 'swr';
import { useParams } from 'react-router-dom';
import { Modal, Select, message } from 'antd';
import { getRoles } from '../../../api';
import { useTranslation } from '../../../../TranslationContext';


interface UserListProps {
  users: any[];
  loading: boolean;
  error: any;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onAddUser: () => void;
  onEditUser: (user: any) => void;
  onDeleteUser: (userId: string) => void;
  onLockUser: (userId: string) => void;
  onUnlockUser: (userId: string) => void;
}

const UserList: React.FC<UserListProps> = ({
  users,
  loading,
  error,
  searchTerm,
  onSearchChange,
  onAddUser,
  onEditUser,
  onDeleteUser,
  onLockUser,
  onUnlockUser,
}) => {
  const { t } = useTranslation();
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [addRoleModalVisible, setAddRoleModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const { appId } = useParams<{ appId: string }>();
  const companyId = localStorage.getItem('company') || '';
  const applictionId = appId;


  if (!appId ) {
    return (
      <div className="text-red-500 p-4">
        {t('missing_application_id')}
      </div>
    );
  }
  if (!applictionId ) {
    return (
      <div className="text-red-500 p-4">
        {t('missing_application_id')}
      </div>
    );
  }
 
  const loadAvailableRoles = async () => {
    try {
      setRolesLoading(true);
      const response = await getRoles(applictionId);
      setAvailableRoles(response.data || []);
    } catch (err) {
      message.error(t('error_loading_roles'));
      console.error(t('error_loading_roles_log'), err);
    } finally {
      setRolesLoading(false);
    }
  };

  const handleAddRole = async (user: any) => {
    setSelectedUser(user);
    setSelectedRole('');
    setAddRoleModalVisible(true);
    await loadAvailableRoles();
  };

  const handleRoleSubmit = async () => {
    if (!selectedUser || !selectedRole) {
      message.warning(t('please_select_role'));
      return;
    }

    try {
      await addUserRole(companyId, appId, selectedUser.id, selectedRole);
      mutate(getUsersUrl(companyId, appId));
      message.success(t('role_added_success'));
      setAddRoleModalVisible(false);
    } catch (err) {
      message.error(t('error_adding_role'));
      console.error(t('error_adding_role_log'), err);
    }
  };
 
  const toggleUserDetails = (userId: string) => {
    setExpandedUserId(expandedUserId === userId ? null : userId);
  };

  if (loading) return (
    <div className="flex justify-center py-8 text-slate-400">
      <div className="animate-pulse">{t('loading_users')}</div>
    </div>


  );console.log('users',users)
  
  if (error) return (
    <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg mb-6 shadow-sm">
      {t('error_loading_users')}
    </div>

  );

  return (
    <div className="space-y-8">
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <h1 className="text-2xl font-semibold text-slate-700">{t('users_list')}</h1>
        <div className="flex items-center space-x-4 mt-4 sm:mt-0">
          <input
            type="text"
            placeholder={t('search_users')}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-all duration-200 w-full sm:w-auto"
          />
          <button
            onClick={onAddUser}
            className="inline-flex items-center px-5 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gray-500 hover:bg-gray900-90 transition-colors duration-200"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('add_users')}
          </button>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-xl overflow-hidden border border-slate-100">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                {t('email')}
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                {t('full_name')}
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                {t('roles')}
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                {t('status')}
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                {t('actions')}
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                {t('details')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-100">
            {users.map((user) => (
              <React.Fragment key={user.id}>
                <tr className="hover:bg-slate-50 transition-colors duration-150">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700 font-medium">
                    {user.fullName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1.5">
                      {user.roles?.map((role: any) => (
                        <UserRoleBadge key={role} role={role} />
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2.5 py-1 text-xs rounded-full font-medium ${
                      user.isLocked 
                        ? 'bg-red-50 text-c06a70 border border-c06a70 border-opacity-20' 
                        : 'bg-slate-50 text-slate-600 border border-slate-200'
                    }`}>
                      {t(`${user.status.toLowerCase()}`)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex space-x-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddRole(user);
                        }}
                        className="text-slate-400 hover:text-slate-700 transition-colors p-1.5 rounded-full hover:bg-slate-100"
                        title={t('add_role')}
                        aria-label={t('add_role')}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditUser(user);
                        }}
                        className="text-slate-400 hover:text-slate-700 transition-colors p-1.5 rounded-full hover:bg-slate-100"
                        title={t('edit')}
                        aria-label={t('edit')}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      {user.status === 'Locked' ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onUnlockUser(user.id);
                          }}
                          className="text-slate-400 hover:text-green-600 transition-colors p-1.5 rounded-full hover:bg-green-50"
                          title={t('unlock')}
                          aria-label={t('unlock')}
                        >
                          <Unlock className="h-4 w-4" />
                        </button>
                      ) : user.status === 'Active' ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onLockUser(user.id);
                          }}
                          className="text-slate-400 hover:text-c06a70 transition-colors p-1.5 rounded-full hover:bg-red-50"
                          title={t('lock')}
                          aria-label={t('lock')}
                        >
                          <Lock className="h-4 w-4" />
                        </button>
                      ) : ''}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteUser(user.id);
                        }}
                        className="text-slate-400 hover:text-c06a70 transition-colors p-1.5 rounded-full hover:bg-red-50"
                        title={t('delete')}
                        aria-label={t('delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleUserDetails(user.id);
                      }}
                      className={`text-slate-400 hover:text-slate-700 transition-colors p-1.5 rounded-full hover:bg-slate-100 ${
                        expandedUserId === user.id ? 'bg-slate-100' : ''
                      }`}
                      title={t('view_details')}
                      aria-label={t('view_details')}
                    >
                      {expandedUserId === user.id ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  </td>
                </tr>
                {expandedUserId === user.id && (
                  <tr>
                    <td colSpan={6} className="p-0">
                      <div className="px-6 py-5 bg-slate-50 border-t border-b border-slate-100 shadow-inner">
                        <UserDetails 
                          user={user} 
                          onClose={() => setExpandedUserId(null)} 
                          companyId={companyId}
                          applicationId={appId} 
                          onRoleDeleted={() => mutate(getUsersUrl(companyId, appId))}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <Modal
        title={t('add_role_to_user', { user: selectedUser?.fullName || t('user') })}
        open={addRoleModalVisible}
        onCancel={() => setAddRoleModalVisible(false)}
        onOk={handleRoleSubmit}
        okText={t('add')}
        cancelText={t('cancel')}
        confirmLoading={rolesLoading}
      >
        <div className="mt-4">
          <label className="block text-sm font-medium text-slate-600 mb-2">
            {t('select_role')}
          </label>
          <Select
            value={selectedRole || undefined}
            onChange={(value) => setSelectedRole(value)}
            style={{ width: '100%' }}
            placeholder={t('select_role_placeholder')}
            loading={rolesLoading}
          >
            {availableRoles.map((role) => (
              <Select.Option key={role.name} value={role.name}>
                {role.name}
              </Select.Option>
            ))}
          </Select>
        </div>
      </Modal>
    </div>
  );
};

export default UserList;
